import { useRef, useEffect } from 'react'
import { usePlayerStore } from '../store/usePlayerStore'

interface Segment {
  start: number
  end: number
}

interface AudioPlayerProps {
  url: string
  segments?: Segment[]
}

export default function AudioPlayer({ url, segments }: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null)
  const {
    isPlaying,
    currentTime,
    setPlaying,
    setCurrentTime,
    setDuration,
    setAudioUrl,
    seekRequest,
    clearSeekRequest,
    seekTo,
    pauseRequest,
    clearPauseRequest,
  } = usePlayerStore()

  useEffect(() => {
    setAudioUrl(url)
  }, [url, setAudioUrl])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime)
    const handleDurationChange = () => setDuration(audio.duration)
    const handleEnded = () => setPlaying(false)

    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('durationchange', handleDurationChange)
    audio.addEventListener('ended', handleEnded)

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('durationchange', handleDurationChange)
      audio.removeEventListener('ended', handleEnded)
    }
  }, [setCurrentTime, setDuration, setPlaying])

  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    if (isPlaying) {
      audio.play()
    } else {
      audio.pause()
    }
  }, [isPlaying])

  // Handle pause requests
  useEffect(() => {
    const audio = audioRef.current
    if (!audio || !pauseRequest) return

    audio.pause()
    clearPauseRequest()
  }, [pauseRequest, clearPauseRequest])

  // Handle seek requests from clicking on segments
  useEffect(() => {
    const audio = audioRef.current
    if (!audio || seekRequest === null) return

    const doSeek = () => {
      audio.currentTime = seekRequest
      clearSeekRequest()
      // Always play after seeking from segment click
      audio.play().catch(() => {})
      setPlaying(true)
    }

    // If audio is ready, seek immediately
    if (audio.readyState >= 1) {
      doSeek()
    } else {
      // Wait for audio to be ready
      audio.addEventListener('loadedmetadata', doSeek, { once: true })
    }
  }, [seekRequest, clearSeekRequest, setPlaying])

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const { duration, activeSegmentIndex } = usePlayerStore()
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0

  // Calculate active segment position on progress bar
  const activeSegment = segments && activeSegmentIndex !== null ? segments[activeSegmentIndex] : null
  const segmentStart = activeSegment && duration > 0 ? (activeSegment.start / duration) * 100 : 0
  const segmentWidth = activeSegment && duration > 0 ? ((activeSegment.end - activeSegment.start) / duration) * 100 : 0

  return (
    <div className="bg-white rounded-lg shadow p-4 sticky top-0 z-10">
      <audio ref={audioRef} src={url} preload="metadata" />

      <div className="flex items-center gap-4">
        <button
          onClick={() => setPlaying(!isPlaying)}
          className="w-12 h-12 flex items-center justify-center bg-primary-500 text-white rounded-full hover:bg-primary-600 transition"
        >
          {isPlaying ? (
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <rect x="6" y="4" width="4" height="16" />
              <rect x="14" y="4" width="4" height="16" />
            </svg>
          ) : (
            <svg className="w-6 h-6 ml-1" fill="currentColor" viewBox="0 0 24 24">
              <polygon points="5,3 19,12 5,21" />
            </svg>
          )}
        </button>

        <div className="flex-1">
          <div
            className="h-2 bg-gray-200 rounded-full cursor-pointer relative"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect()
              const percent = (e.clientX - rect.left) / rect.width
              seekTo(percent * duration)
            }}
          >
            {/* Active segment highlight */}
            {activeSegment && (
              <div
                className="absolute h-full bg-orange-200 rounded-full"
                style={{
                  left: `${segmentStart}%`,
                  width: `${segmentWidth}%`
                }}
              />
            )}
            {/* Progress bar */}
            <div
              className="absolute h-full bg-primary-500 rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <div className="text-sm text-gray-600 min-w-[80px] text-right">
          {formatTime(currentTime)} / {formatTime(duration)}
        </div>
      </div>
    </div>
  )
}
